import apiClient from './client';
import type { AdminPermission } from '../config/admin-permissions';

export type AdminUser = {
  id: number;
  name: string | null;
  phone: string | null;
  telegramUsername: string | null;
  telegramId: string;
  createdAt: string;
  source: string | null;
  orderCount: number;
  totalSum: number;
  bonusBalance: number;
};

export type AdminUsersParams = {
  from?: string;
  to?: string;
  source?: string;
  page?: number;
  limit?: number;
  sortBy?: 'created_at' | 'total_sum' | 'order_count' | 'bonus_balance';
  sortOrder?: 'asc' | 'desc';
  search?: string;
};

export type AdminUsersResponse = {
  data: AdminUser[];
  pagination?: { page: number; limit: number; total: number; totalPages: number };
};
export type AdminUserDetails = {
  id: number;
  telegramId: string;
  telegramUsername: string | null;
  name: string | null;
  phone: string | null;
  source: string | null;
  bonusBalance: number;
  createdAt: string;
  orderCount: number;
  totalSum: number;
  paidOrderCount: number;
  paidTotalSum: number;
  lastOrderAt: string | null;
  recentOrders: Array<{
    id: number;
    status: string | null;
    paymentStatus: string | null;
    total: number;
    createdAt: string;
  }>;
};

export type AdminMetrics = {
  newUsersToday: number;
  newUsers7Days: number;
  newUsers30Days: number;
  newUsersPeriod: number;
  totalRevenue: number;
  cogs?: number;
  grossProfit?: number;
  grossMarginPercent?: number;
  avgCheck: number;
  conversion: number;
  conversionToOrder?: number;
  conversionToPayment?: number;
  conversionToPaymentFromStart?: number;
  orderCount: number;
  userCount: number;
  arpu?: number;
  revenuePerDayAvg?: number;
  activeClients?: number;
  retention7Days?: number;
  retention30Days?: number;
};

export type AdminComparison = {
  newUsersPercent: number;
  revenuePercent: number;
  orderCountPercent: number;
  grossProfitPercent?: number;
} | null;

export type AdminChartPoint = { date: string; count: number };
export type AdminChartRevenue = { date: string; total: number };
export type AdminChartAvgCheck = { date: string; avgCheck: number; orderCount: number };
export type AdminSource = { source: string; count: number };
export type AdminUserBySource = { source: string; count: number; percent: number };
export type AdminTopClient = { userId: number; totalSum: number; telegramUsername: string | null };
export type AdminCohortRow = { cohortMonth: string; cohortSize: number; repeatCount: number; repeatPercent: number };
export type AdminFunnelStep = { label: string; count: number; percent: number };
export type AdminSourcePerformance = {
  source: string;
  transitionsPeriod: number;
  transitionsTotal: number;
  ordersPeriod: number;
  ordersTotal: number;
  revenuePeriod: number;
  revenueTotal: number;
};

export type AdminAnalyticsData = {
  metrics: AdminMetrics;
  comparison: AdminComparison;
  periodFrom?: string;
  periodTo?: string;
  charts: {
    usersGrowth: AdminChartPoint[];
    trafficSources: AdminSource[];
    revenueByDay: AdminChartRevenue[];
    avgCheckByDay: AdminChartAvgCheck[];
    chartBucket?: '2h' | 'day';
  };
  marketing: {
    usersBySourcePercent: AdminUserBySource[];
    repeatPurchasesPercent: number;
    avgOrdersPerClient: number;
    ltv: number;
    sourcePerformance?: AdminSourcePerformance[];
    cohortAnalysis?: AdminCohortRow[];
    funnel?: AdminFunnelStep[];
    clientsWithoutOrdersPercent?: number;
    cacPlaceholder?: number | null;
  };
  top10Clients?: AdminTopClient[];
  topSources?: AdminSource[];
  avgOrderIntervalDays?: number | null;
};

export type AdminTrafficLink = { code: string; label: string; hint: string; url: string };

// Feature flags and role from backend (for conditional UI)
export type AdminConfig = {
  trafficLinks?: AdminTrafficLink[];
  access?: {
    catalog: boolean;
    main: boolean;
  };
  modules?: {
    dashboard: boolean;
    users: boolean;
    export: boolean;
    forecast: boolean;
    marketing?: boolean;
    promocodes?: boolean;
  };
  dashboardEnabled?: boolean;
  features: {
    cohorts: boolean;
    funnel: boolean;
    retention30: boolean;
    top10: boolean;
    avgInterval: boolean;
    exportXlsx: boolean;
    forecast?: boolean;
  };
  permissions?: AdminPermission[];
};

export type AdminForecastPoint = { date: string; value: number; lower: number; upper: number };
export type AdminForecastData = {
  historical: { date: string; value: number }[];
  forecast: AdminForecastPoint[];
  mape14d: number | null;
  summary?: { tomorrow: number; week: number; month: number };
  tips?: string[];
};
export type AdminAttributionHealth = {
  status: 'ok' | 'degraded';
  counters: {
    pendingActive: number;
    pendingExpiredUnconsumed: number;
    usersWithPrimarySource: number;
    usersWithoutPrimarySource: number;
  };
  diagnostics?: {
    driftSummary?: {
      errorsCount?: number;
      warningsCount?: number;
    };
    operatorHints?: string[];
  };
};

export async function getAdminConfig(): Promise<AdminConfig> {
  const res = await apiClient.get<{ success: true; data: AdminConfig }>('/api/admin/config');
  return (res.data?.data ?? { features: {}, dashboardEnabled: true }) as AdminConfig;
}

/** Таймаут для загрузки изображений и операций с каталогом (90 сек). */
export const ADMIN_CATALOG_TIMEOUT_MS = 90000;

export async function uploadAdminProductImage(payload: {
  fileName: string;
  contentType: string;
  dataBase64: string;
}): Promise<string> {
  const res = await apiClient.post<{ success: true; data: { url: string } }>(
    '/api/admin/catalog/products/upload-image',
    payload,
    { timeout: ADMIN_CATALOG_TIMEOUT_MS }
  );
  return res.data?.data?.url ?? '';
}

export async function getAdminUsers(params: AdminUsersParams): Promise<AdminUsersResponse> {
  const q = new URLSearchParams();
  if (params.from) q.set('from', params.from);
  if (params.to) q.set('to', params.to);
  if (params.source) q.set('source', params.source);
  if (params.page != null) q.set('page', String(params.page));
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.sortBy) q.set('sortBy', params.sortBy);
  if (params.sortOrder) q.set('sortOrder', params.sortOrder);
  if (params.search) q.set('search', params.search);
  const res = await apiClient.get<{ success: true; data: AdminUser[]; pagination?: AdminUsersResponse['pagination'] }>(
    `/api/admin/users?${q.toString()}`
  );
  return {
    data: res.data?.data ?? [],
    pagination: res.data?.pagination,
  };
}

export async function getAdminUserDetails(id: number): Promise<AdminUserDetails> {
  const res = await apiClient.get<{ success: true; data: AdminUserDetails }>(`/api/admin/users/${id}`);
  return res.data?.data as AdminUserDetails;
}

export async function getAdminAnalytics(params: { from?: string; to?: string; period?: 'all' }): Promise<AdminAnalyticsData> {
  const q = new URLSearchParams();
  if (params.period === 'all') {
    q.set('period', 'all');
  } else {
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
  }
  const res = await apiClient.get<{ success: true; data: AdminAnalyticsData }>(
    `/api/admin/analytics?${q.toString()}`
  );
  return (res.data?.data ?? {}) as AdminAnalyticsData;
}

export async function getAdminForecast(horizon: 1 | 7 | 14 | 30): Promise<AdminForecastData> {
  const res = await apiClient.get<{ success: true; data: AdminForecastData }>(
    `/api/admin/forecast?horizon=${horizon}`
  );
  return (res.data?.data ?? { historical: [], forecast: [], mape14d: null }) as AdminForecastData;
}

export async function getAdminAttributionHealth(): Promise<AdminAttributionHealth> {
  const res = await apiClient.get<{ success: true; data: AdminAttributionHealth }>('/api/admin/attribution-health');
  return res.data?.data as AdminAttributionHealth;
}

export async function downloadAdminExport(month: string): Promise<Blob> {
  const res = await apiClient.get<Blob>(`/api/admin/export?month=${encodeURIComponent(month)}`, {
    responseType: 'blob',
  });
  return res.data as unknown as Blob;
}

export type ExportPeriodParams = {
  from: string;
  to: string;
  format: 'csv' | 'xlsx';
  type: 'all' | 'active' | 'no_orders' | 'repeat';
};

export async function downloadAdminExportPeriod(params: ExportPeriodParams): Promise<Blob> {
  const q = new URLSearchParams({
    from: params.from,
    to: params.to,
    format: params.format,
    type: params.type,
  });
  const res = await apiClient.get<Blob>(`/api/admin/export-period?${q.toString()}`, {
    responseType: 'blob',
  });
  return res.data as unknown as Blob;
}

export async function downloadAdminExportUsersReport(format: 'csv' | 'xlsx'): Promise<Blob> {
  const res = await apiClient.get<Blob>(`/api/admin/export/users-report?format=${format}`, {
    responseType: 'blob',
  });
  return res.data as unknown as Blob;
}

export type UserReportRow = {
  name: string;
  phone: string;
  telegram: string;
  source?: string;
  createdAt: string;
  orderCount: number;
  orderCountPaid: number;
  totalSum: number;
  totalPaid: number;
  paidCogs?: number;
  grossProfit?: number;
  grossMarginPercent?: number;
};

export type AdminPromocode = {
  id: number;
  code: string;
  description: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  minOrderSum: number;
  usageLimit: number | null;
  usageCount: number;
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

export type AdminAuditLogEntry = {
  id: string;
  actorTelegramId: string | null;
  role: string | null;
  actionType: string | null;
  entityType: string | null;
  entityId: string | null;
  entityLabel: string | null;
  reason: string | null;
  changeSummary: string | null;
  status: 'success' | 'failure';
  statusCode: number;
  durationMs: number | null;
  method: string;
  path: string;
  createdAt: string;
};

export type AdminAuditLogFilters = {
  actionType?: string;
  entityType?: string;
  from?: string;
  to?: string;
  q?: string;
  limit?: number;
  beforeId?: string;
};
export type AdminAuditLogResponse = {
  data: AdminAuditLogEntry[];
  pagination?: {
    limit: number;
    hasMore: boolean;
    nextBeforeId: string | null;
  };
};

export async function getAdminExportUsersReportJson(): Promise<UserReportRow[]> {
  const res = await apiClient.get<{ success: true; data: UserReportRow[] }>('/api/admin/export/users-report?format=json');
  return res.data?.data ?? [];
}

export async function getAdminPromocodes(): Promise<AdminPromocode[]> {
  const res = await apiClient.get<{ success: true; data: AdminPromocode[] }>('/api/admin/promocodes');
  return res.data?.data ?? [];
}

export async function createAdminPromocode(payload: {
  code: string;
  description?: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  minOrderSum?: number;
  usageLimit?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isActive?: boolean;
}): Promise<void> {
  await apiClient.post('/api/admin/promocodes', payload);
}

export async function updateAdminPromocode(
  id: number,
  payload: Partial<{
    code: string;
    description: string;
    discountType: 'percent' | 'fixed';
    discountValue: number;
    minOrderSum: number;
    usageLimit: number | null;
    startsAt: string | null;
    endsAt: string | null;
    isActive: boolean;
  }>
): Promise<void> {
  await apiClient.patch(`/api/admin/promocodes/${id}`, payload);
}

export async function deleteAdminPromocode(id: number, reason?: string): Promise<void> {
  await apiClient.delete(`/api/admin/promocodes/${id}`, {
    data: reason ? { reason } : undefined,
  });
}

export async function runAdminFloriaSync(reason?: string): Promise<void> {
  await apiClient.post('/api/admin/floria/sync', reason ? { reason } : {});
}

export async function getAdminAuditLog(filters: AdminAuditLogFilters = {}): Promise<AdminAuditLogResponse> {
  const q = new URLSearchParams();
  if (filters.actionType) q.set('actionType', filters.actionType);
  if (filters.entityType) q.set('entityType', filters.entityType);
  if (filters.from) q.set('from', filters.from);
  if (filters.to) q.set('to', filters.to);
  if (filters.q) q.set('q', filters.q);
  if (filters.limit != null) q.set('limit', String(filters.limit));
  if (filters.beforeId) q.set('beforeId', filters.beforeId);
  const res = await apiClient.get<{ success: true; data: { items: AdminAuditLogEntry[]; pageInfo?: AdminAuditLogResponse['pagination'] } }>(`/api/admin/audit-log?${q.toString()}`);
  return {
    data: res.data?.data?.items ?? [],
    pagination: res.data?.data?.pageInfo,
  };
}

